'use client';

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Lock,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Eye,
  Shield,
  ShieldOff,
  ArrowUpDown,
} from 'lucide-react';
import { PolicyFilters, PolicyFiltersState, applyPolicyFilters } from './PolicyFilters';

export interface CedarPolicy {
  id: string;
  organizationId?: string;
  name: string;
  description?: string;
  policyText: string;
  policyType?: string;
  resourceType?: string;
  isDefault: boolean;
  isActive?: boolean;
  priority: number;
  createdAt: string;
  createdBy?: string;
}

export interface PolicyListProps {
  policies: CedarPolicy[];
  onEdit: (policy: CedarPolicy) => void;
  onDelete: (id: string) => void;
  onDuplicate?: (policy: CedarPolicy) => void;
  onViewDetails?: (policy: CedarPolicy) => void;
}

type SortField = 'name' | 'priority' | 'createdAt';
type SortDirection = 'asc' | 'desc';

export function PolicyList({
  policies,
  onEdit,
  onDelete,
  onDuplicate,
  onViewDetails
}: PolicyListProps) {
  const [filters, setFilters] = useState<PolicyFiltersState>({
    search: '',
    effectType: 'all',
    resourceType: null,
    showDefaults: true
  });
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [policyToDelete, setPolicyToDelete] = useState<CedarPolicy | null>(null);

  const filteredAndSortedPolicies = useMemo(() => {
    const filtered = applyPolicyFilters(policies, filters);

    return [...filtered].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'priority':
          comparison = a.priority - b.priority;
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [policies, filters, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleDeleteClick = (policy: CedarPolicy) => {
    setPolicyToDelete(policy);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (policyToDelete) {
      onDelete(policyToDelete.id);
    }
    setDeleteDialogOpen(false);
    setPolicyToDelete(null);
  };

  const getEffectBadge = (policyText: string) => {
    const isPermit = policyText.trim().startsWith('permit');
    return isPermit ? (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
        <Shield className="h-3 w-3 mr-1" />
        Allow
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <ShieldOff className="h-3 w-3 mr-1" />
        Deny
      </Badge>
    );
  };

  const getResourceFromPolicy = (policyText: string): string | null => {
    const match = policyText.match(/resource\s+is\s+([\w:]+)/);
    return match ? match[1].split('::')[1] || match[1] : null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Active Policies</CardTitle>
        <CardDescription>
          {filteredAndSortedPolicies.length} of {policies.length} policies shown
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PolicyFilters filters={filters} onFiltersChange={setFilters} />

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="w-[300px]">
                  <button
                    className="flex items-center gap-1 font-medium"
                    onClick={() => handleSort('name')}
                  >
                    Policy Name
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-[100px]">Type</TableHead>
                <TableHead className="w-[120px]">Resource</TableHead>
                <TableHead className="w-[80px]">
                  <button
                    className="flex items-center gap-1 font-medium"
                    onClick={() => handleSort('priority')}
                  >
                    Priority
                    <ArrowUpDown className="h-3 w-3" />
                  </button>
                </TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedPolicies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    {policies.length === 0
                      ? 'No policies defined yet.'
                      : 'No policies match your filters.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredAndSortedPolicies.map(policy => (
                  <TableRow key={policy.id} className="group">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {policy.isDefault && (
                          <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">
                            {policy.name}
                          </div>
                          {policy.description && (
                            <div className="text-xs text-slate-500 truncate max-w-[250px]">
                              {policy.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getEffectBadge(policy.policyText)}</TableCell>
                    <TableCell>
                      {getResourceFromPolicy(policy.policyText) ? (
                        <Badge variant="secondary" className="font-mono text-xs">
                          {getResourceFromPolicy(policy.policyText)}
                        </Badge>
                      ) : (
                        <span className="text-slate-400 text-sm">All</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`font-mono ${
                          policy.priority >= 80
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : policy.priority >= 50
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {policy.priority}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {policy.isDefault ? (
                        <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                          System
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                          Custom
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {onViewDetails && (
                            <DropdownMenuItem onClick={() => onViewDetails(policy)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                          )}
                          {!policy.isDefault && (
                            <DropdownMenuItem onClick={() => onEdit(policy)}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          {onDuplicate && (
                            <DropdownMenuItem onClick={() => onDuplicate(policy)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                          )}
                          {!policy.isDefault && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600 focus:text-red-600"
                                onClick={() => handleDeleteClick(policy)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{policyToDelete?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
